import { watchImmediate } from '@vueuse/core';
import { Texture, TextureLoader } from 'three';
import { stopAll, type StopHandle, taggedLogger } from '@/util';
import { loadImageAsUrl } from '@/api/loadImage';
import type { Url } from '@/domain/types';
import { markRaw, type Raw } from 'vue';

const log = taggedLogger('3D');

/**
 * Reactive state for a [texture]{@link https://threejs.org/docs/#Texture}
 */
export type TextureState = {
    /** The source url for the texture */
    source: Url | null;

    /**
     * A version number for the source. The actual value does not matter: changing it
     * forces an update of the texture when the image linked by the source-url changes
     * without the source-url itself value changing.
     */
    sourceVersion: number;

    /**
     * The result texture
     */
    texture: Raw<Texture> | null;
};

/**
 * Create the reactive state for a [texture]{@link https://threejs.org/docs/#Texture}
 */
export function textureState(properties?: Partial<TextureState>): TextureState {
    return {
        source: properties?.source ?? null,
        sourceVersion: properties?.sourceVersion ?? 0,
        texture: null,
    };
}

/**
 * Update the reactive state for a [texture]{@link https://threejs.org/docs/#Texture}
 */
export function updateTexture(state: TextureState): StopHandle {
    const loader = _textureLoader();
    let stopLoading: StopHandle = () => {};

    const disposeTexture = () => {
        if (state.texture !== null) {
            state.texture.dispose();
            state.texture = null;
        }
    };

    return stopAll(
        watchImmediate(
            () => [state.source, state.sourceVersion],
            async () => {
                stopLoading();
                disposeTexture();
                if (state.source === null) {
                    return;
                }

                log.info('Loading texture from %s', state.source);
                const controller = new AbortController();
                const signal = controller.signal;
                stopLoading = () => controller.abort();

                try {
                    const data = await loadImageAsUrl(state.source, { signal });
                    if (!data) {
                        log.warning('Failed to load image from %s', state.source);
                        return;
                    }
                    log.debug('Loaded texture from %s', state.source);
                    state.texture = markRaw(loader.load(data));
                } catch (error) {
                    if (!signal.aborted) {
                        throw error;
                    }
                }
            },
        ),
        () => {
            stopLoading();
            disposeTexture();
        },
    );
}

let _textureLoaderInstance: TextureLoader | null = null;

function _textureLoader(): TextureLoader {
    if (_textureLoaderInstance === null) {
        _textureLoaderInstance = new TextureLoader();
    }
    return _textureLoaderInstance;
}
